// PHASE_4b.6 — TopBar bell notification inbox (list + mark-read).
//
// Trigger sits in TopBar.tsx next to the AI-drawer toggle. Data:
// `useInfiniteQuery` over `fetchInboxPage` (keyset-paginated — see
// inbox-api.ts header for the pagination + unread-count gotchas). The query
// runs on mount (so the unread badge is correct even before the popover ever
// opens) and force-refetches when the popover opens so a just-arrived
// notification shows without a manual reload.
//
// The unread badge count is the SERVER's count — a `state=unread&limit=1`
// fetch whose `total` is the exact unread total (see inbox-api.ts header). It
// used to be derived from loaded pages, which under-counted anyone with more
// unread rows than one page; that was a workaround for a server bug fixed long
// before this comment was.
//
// The popover is a peek, not the inbox: it shows the newest page and hands off
// to /notifications for filtering and paging.
//
// Popover positioning is caller-computed (packages/components Popover ships
// no auto-placement yet, Phase 4b.5) — the trigger's
// `getBoundingClientRect()` is read at open-time and the panel is anchored
// below the bell, right-aligned, with a viewport-left clamp.

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Bell } from "lucide-react";

import { Empty, ErrorState, IconButton, Loading, Popover } from "@novemai/components";

import { cn } from "@/lib/utils";
import { useTimeZone } from "@/api/use-time-zone";
import {
  fetchInboxPage,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  UNREAD_COUNT_QUERY_KEY,
  type InboxNotification,
  type InboxPage,
} from "./inbox-api";

const PANEL_WIDTH = 360;
const PANEL_GAP = 8;
const UNREAD_BADGE_CAP = 9;
// Paused by TanStack while the tab is hidden (refetchIntervalInBackground defaults off).
const UNREAD_POLL_MS = 60_000;

const QUERY_KEY = ["notifications", "inbox"] as const;

function isUnread(n: InboxNotification): boolean {
  return n.status !== "read";
}

function formatQueuedAt(iso: string | null, locale: string, timeZone: string): string {
  if (iso === null) return "";
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short", timeZone }).format(
    new Date(iso),
  );
}

export function NotificationBell(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const [open, setOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const query = useInfiniteQuery({
    queryKey: QUERY_KEY,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      fetchInboxPage({ cursor: pageParam }),
    // The server answers "is there a next page" with the cursor itself
    // (SEM-5327), so this is a fact rather than a count comparison.
    getNextPageParam: (lastPage: InboxPage) => lastPage.nextCursor ?? undefined,
    staleTime: 30_000,
  });

  // The bell mounts once with the shell and lives for the whole session, so a
  // notification that arrives after mount used to leave the badge one behind
  // until the popover was opened: the inbox screen, which fetches fresh on
  // mount, then showed a bigger number than the bell beside it. The app-wide
  // default turns window-focus refetch off (queryClient.ts) and points at this
  // badge as the surface that opts back in, but it never did. A poll covers the
  // tab that stays in front; focus covers the one that was in the background.
  const unreadQuery = useQuery({
    queryKey: UNREAD_COUNT_QUERY_KEY,
    queryFn: fetchUnreadCount,
    staleTime: 30_000,
    refetchInterval: UNREAD_POLL_MS,
    refetchOnWindowFocus: true,
  });

  // No dedup pass: under keyset paging (SEM-5327) a row cannot appear on two
  // pages. Each page is fetched with the cursor of the row before it, so a
  // notification arriving mid-scroll lands ABOVE the first cursor and cannot
  // slide an already-loaded row into the next window — which is exactly what
  // offset paging did, and what the dedup here used to hide.
  const rows: InboxNotification[] = query.data?.pages.flatMap((page) => [...page.notifications]) ?? [];
  const unreadCount = unreadQuery.data ?? 0;

  const markReadMutation = useMutation({
    mutationFn: (deliveryId: string) => markNotificationRead(deliveryId),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_QUERY_KEY });
    },
  });

  // Panel grup filtresi taşımaz; hedef küme başlıktaki sayacın saydığı küme,
  // yani çağıranın bütün okunmamışlarıdır (inbox ekranı filtreyi gövdeye koyar).
  const markAllMutation = useMutation({
    mutationFn: () => markAllNotificationsRead({ state: "unread" }),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const openPopover = (): void => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect !== undefined) {
      setPanelPosition({
        top: rect.bottom + PANEL_GAP,
        left: Math.max(PANEL_GAP, rect.right - PANEL_WIDTH),
      });
    }
    setOpen(true);
    void query.refetch();
    void unreadQuery.refetch();
  };

  const handleRowClick = (notification: InboxNotification): void => {
    if (isUnread(notification)) {
      markReadMutation.mutate(notification.delivery_id);
    }
    if (notification.link !== null && notification.link.startsWith("/")) {
      void navigate(notification.link);
      setOpen(false);
    }
  };

  const badgeLabel =
    unreadCount > UNREAD_BADGE_CAP
      ? t("notifications.inbox.badge_count_capped", { count: UNREAD_BADGE_CAP })
      : String(unreadCount);

  return (
    <div className="relative">
      <IconButton
        ref={triggerRef}
        aria-label={t("notifications.inbox.bell_aria")}
        variant="ghost"
        size="md"
        onClick={openPopover}
        className="relative"
      >
        <Bell size={16} aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            role="status"
            aria-live="polite"
            className="absolute -right-1.5 -top-1.5"
          >
            {/* NOT <Badge>: that is a 23x19 inline status PILL, and a 32px
                IconButton centres a 16px glyph — anchored on the corner it
                landed squarely on the bell's top-right quadrant and hid the
                icon it was counting. A count bubble is its own shape: 16px
                circle, SOLID accent (the pill's accent-50 tint reads as a
                smear over a glyph), and a ring in the bar's own background so
                it detaches from the icon instead of merging with it. */}
            <span
              aria-hidden="true"
              className={cn(
                "flex h-4 min-w-4 items-center justify-center rounded-full px-1",
                "bg-[var(--accent)] text-[10px] font-semibold leading-none tabular-nums",
                "text-[var(--fg-on-accent)] ring-2 ring-[var(--bg-surface)]",
              )}
            >
              {badgeLabel}
            </span>
            {/* The sr-only sentence is the ONE announced text node — otherwise
                AT reads "2, 2 unread notifications". */}
            <span className="sr-only">
              {t("notifications.inbox.unread_count_status", { count: unreadCount })}
            </span>
          </span>
        )}
      </IconButton>

      <Popover
        open={open}
        onClose={(): void => setOpen(false)}
        onKeyDownCapture={(event): void => {
          // Escape dismisses via Popover; keep keyboard navigation at its
          // trigger without stealing focus on outside clicks or row navigation.
          if (event.key === "Escape") triggerRef.current?.focus();
        }}
        aria-label={t("notifications.inbox.panel_label")}
        style={{
          position: "fixed",
          top: panelPosition.top,
          left: panelPosition.left,
          width: PANEL_WIDTH,
          maxHeight: 420,
          overflowY: "auto",
        }}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--border-subtle)] px-4 py-3">
          <div className="min-w-0">
            <h2 className="font-serif text-[15px] font-medium text-[var(--fg-primary)]">
              {t("notifications.inbox.title")}
            </h2>
            {unreadQuery.isSuccess && (
              <p className="text-[12px] text-[var(--fg-muted)]">
                {t("notifications.inbox.unread_subtitle", { count: unreadCount })}
              </p>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              disabled={markAllMutation.isPending}
              onClick={(): void => markAllMutation.mutate()}
              className="shrink-0 text-[12.5px] font-semibold text-[var(--accent)] disabled:opacity-50"
            >
              {t("notifications.inbox.mark_all_read")}
            </button>
          )}
        </div>
        {markAllMutation.isError && (
          <p role="alert" className="border-b border-[var(--border-subtle)] px-4 py-2 text-[12px] text-[var(--state-danger)]">
            {t("notifications.inbox.mark_all_read_error_title")}
          </p>
        )}

        {query.isLoading ? (
          <div className="p-4">
            <Loading label={t("notifications.inbox.loading")} rows={3} />
          </div>
        ) : query.isError ? (
          <div className="p-4">
            <ErrorState
              title={t("notifications.inbox.error_title")}
              action={
                <button
                  type="button"
                  onClick={(): void => void query.refetch()}
                  className="text-[13px] font-semibold text-[var(--accent)]"
                >
                  {t("notifications.inbox.retry")}
                </button>
              }
            />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-4">
            <Empty
              title={t("notifications.inbox.empty_title")}
              description={t("notifications.inbox.empty_description")}
            />
          </div>
        ) : (
          <ul>
            {rows.map((notification) => {
              const unread = isUnread(notification);
              return (
                <li key={notification.delivery_id} className="border-b border-[var(--border-subtle)] last:border-b-0">
                  <button
                    type="button"
                    onClick={(): void => handleRowClick(notification)}
                    className={cn(
                      "flex w-full items-start gap-2 px-4 py-3 text-left hover:bg-[var(--bg-subtle)]",
                      unread && "bg-[var(--accent-subtle-bg)]",
                    )}
                  >
                    {unread && (
                      <span
                        aria-hidden="true"
                        className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent)]"
                      />
                    )}
                    <span className="min-w-0 flex-1">
                      {unread && <span className="sr-only">{t("notifications.inbox.unread_sr")}</span>}
                      <span
                        className={cn(
                          "block truncate text-[13px] text-[var(--fg-primary)]",
                          unread && "font-medium",
                        )}
                      >
                        {notification.subject}
                      </span>
                      <span className="line-clamp-2 block text-[12px] text-[var(--fg-muted)]">
                        {notification.body}
                      </span>
                      <span className="mt-1 block text-[11px] text-[var(--fg-muted)]">
                        {formatQueuedAt(notification.queued_at, i18n.language, timeZone)}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex items-center justify-between border-t border-[var(--border-subtle)] p-3">
          <button
            type="button"
            onClick={(): void => {
              setOpen(false);
              void navigate("/notifications");
            }}
            className="text-[13px] font-semibold text-[var(--accent)]"
          >
            {t("notifications.inbox.see_all")}
          </button>
          {query.hasNextPage && (
            <button
              type="button"
              disabled={query.isFetchingNextPage}
              onClick={(): void => void query.fetchNextPage()}
              className="text-[13px] font-semibold text-[var(--accent)] disabled:opacity-50"
            >
              {t("notifications.inbox.load_more")}
            </button>
          )}
        </div>
      </Popover>
    </div>
  );
}
